import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import './Solitaire.css';

// Card suits and values
const suits = ['hearts', 'diamonds', 'clubs', 'spades'];
const values = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

// Get the suit symbol for display
const getSuitSymbol = (suit) => {
  switch (suit) {
    case 'hearts': return '♥';
    case 'diamonds': return '♦';
    case 'clubs': return '♣';
    case 'spades': return '♠';
    default: return '';
  }
};

// Get the color for the suit
const getSuitColor = (suit) => (suit === 'hearts' || suit === 'diamonds') ? 'red' : 'black';

// Create deck of cards
const createDeck = () => {
  let deck = [];
  for (let suit of suits) {
    for (let value of values) {
      deck.push({ suit, value, faceUp: true }); // deck cards are always face-up initially
    }
  }
  return deck;
};

// Shuffle deck
const shuffleDeck = (deck) => {
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
};

// Deal cards into columns and return remaining deck
const dealCards = (deck) => {
  const columns = [[], [], [], [], [], [], []];
  let index = 0;
  for (let i = 0; i < columns.length; i++) {
    for (let j = 0; j <= i; j++) {
      // Only the top card in each column is face-up
      const card = { ...deck[index], faceUp: j === i };
      columns[i].push(card);
      index++;
    }
  }
  return { columns, remainingDeck: deck.slice(index) };
};

const Solitaire = ({
  playtime,
  gamePaused,
  setIsTimerActive,
  soundEffects,
  saveGame,    // Provided from Wrapper.js
  loadGame,    // Provided from Wrapper.js
  gameId       // e.g. "solitaire"
}) => {
  // Our complete game state variables
  const [deck, setDeck] = useState([]);
  const [currentDeckIndex, setCurrentDeckIndex] = useState(-1);
  const [columns, setColumns] = useState([]);
  const [foundations, setFoundations] = useState([[], [], [], []]);
  const [isWin, setIsWin] = useState(false);

  // These states are only for dragging (ephemeral – not saved)
  const [draggedCard, setDraggedCard] = useState(null);
  const [draggedCardPosition, setDraggedCardPosition] = useState({ x: 0, y: 0 });
  const [originalColumn, setOriginalColumn] = useState(null);

  // Resume prompt state
  const [resumePrompt, setResumePrompt] = useState(false);
  const [savedGameData, setSavedGameData] = useState(null);

  // On mount, check if a saved game exists.
  // If one is found, do not initialize new state but prompt the user.
  useEffect(() => {
    const saved = loadGame(gameId);
    if (saved) {
      setResumePrompt(true);
      setSavedGameData(saved);
    } else {
      // No saved game: initialize a new game.
      const newDeck = shuffleDeck(createDeck());
      const { columns: dealtColumns, remainingDeck } = dealCards(newDeck);
      setColumns(dealtColumns);
      setDeck(remainingDeck);
      setCurrentDeckIndex(-1);
    }
  }, [gameId, loadGame]);

  // Auto-save effect: only run when we are not waiting on a resume prompt.
  useEffect(() => {
    if (!resumePrompt) {
      saveGame(gameId, {
        deck,
        currentDeckIndex,
        columns,
        foundations,
        isWin
      });
    }
  }, [deck, currentDeckIndex, columns, foundations, isWin, gameId, saveGame, resumePrompt]);

  // Cleanup on unmount (for extra safety)
  useEffect(() => {
    return () => {
      if (!isWin && deck.length > 0) {
        saveGame(gameId, {
          deck,
          currentDeckIndex,
          columns,
          foundations,
          isWin
        });
        console.log(gameId, {
            deck,
            currentDeckIndex,
            columns,
            foundations,
            isWin
          })
      } else {
        localStorage.removeItem(`savedGame_${gameId}`);
      }
    };
  }, [deck, currentDeckIndex, columns, foundations, isWin, gameId, saveGame]);

  // Handler to resume the saved game.
  const handleResumeGame = () => {
    console.log(savedGameData);
    if (savedGameData) {
      setDeck(savedGameData.deck);
      setCurrentDeckIndex(savedGameData.currentDeckIndex);
      setColumns(savedGameData.columns);
      setFoundations(savedGameData.foundations);
      setIsWin(savedGameData.isWin);
      soundEffects.play('playButton');
    }
    setResumePrompt(false);
  };

  // Handler to start a new game.
  const handleNewGame = () => {
    localStorage.removeItem(`savedGame_${gameId}`);
    setResumePrompt(false);
    const newDeck = shuffleDeck(createDeck());
    const { columns: dealtColumns, remainingDeck } = dealCards(newDeck);
    setColumns(dealtColumns);
    setDeck(remainingDeck);
    setCurrentDeckIndex(-1);
    setFoundations([[], [], [], []]);
    setIsWin(false);
    soundEffects.play('generalButton');
  };

  /*if (resumePrompt) {
    return (
      <div className="solitaire-container"><div className="modal-overlay">
        <div className="modal-content">
            <h2>Resume Saved Game?</h2>
            <p>A saved game was found. Would you like to resume your previous game?</p>
            <button onClick={handleResumeGame}>Resume</button>
            <button onClick={handleNewGame}>New Game</button>
        </div>
        </div>
      </div>
    );
  }*/

  // --- Game Logic Functions (unchanged from your existing code) ---

  const isValidFoundationMove = (card, foundation) => {
    if (foundation.length === 0) return card.value === 'A';
    const topCard = foundation[foundation.length - 1];
    return card.suit === topCard.suit &&
           values.indexOf(card.value) === values.indexOf(topCard.value) + 1;
  };

  const handleFoundationDrop = (foundationIndex, e) => {
    console.log('handleFoundationDrop', foundationIndex);
    if (!draggedCard) return;
    const { cards, fromCol, startIndex, originalDeckIndex } = draggedCard;
    const card = cards[0];
    let newColumns = [...columns];
    let newDeck = [...deck];
    let newFoundations = [...foundations];
    let moveWasValid = false;
    if (isValidFoundationMove(card, newFoundations[foundationIndex])) {
      moveWasValid = true;
      soundEffects.play('card');
      soundEffects.play('goodMove');
      if (fromCol === 'deck') {
        const cardIndex = newDeck.findIndex(
          deckCard => deckCard.suit === card.suit && deckCard.value === card.value
        );
        if (cardIndex !== -1) {
          newDeck.splice(cardIndex, 1);
          setDeck(newDeck);
        }
      } else {
        newColumns[fromCol] = newColumns[fromCol].slice(0, startIndex);
        if (newColumns[fromCol].length > 0) {
          setTimeout(() => {
            setColumns(prevColumns => {
              const updatedColumns = [...prevColumns];
              updatedColumns[fromCol][updatedColumns[fromCol].length - 1].faceUp = true;
              return updatedColumns;
            });
          }, 333);
        }
      }
      const cardWithColor = { ...card, color: getSuitColor(card.suit) };
      newFoundations[foundationIndex] = [...newFoundations[foundationIndex], cardWithColor];
      setFoundations(newFoundations);
      setColumns(newColumns);
      setDraggedCard(null);
      setDraggedCardPosition({ x: 0, y: 0 });
      checkWinCondition();
    } else {
      moveWasValid = false;
      soundEffects.play('card');
      soundEffects.play('badMove');
      if (fromCol === 'deck') {
        setCurrentDeckIndex(originalDeckIndex);
      }
      setDraggedCard(null);
      setDraggedCardPosition({ x: 0, y: 0 });
    }
    console.log('foundation Updated State:', { deck: newDeck, columns: newColumns, currentDeckIndex });
    return moveWasValid;
  };

  const handleDeckClick = () => {
    soundEffects.play('card');
    setCurrentDeckIndex((prevIndex) =>
      prevIndex === deck.length - 1 ? -1 : prevIndex + 1
    );
  };

  const handleDeckTouchStart = (e) => {
    e.preventDefault();
    const card = deck[currentDeckIndex];
    const originalDeckIndex = currentDeckIndex;
    setCurrentDeckIndex((prevIndex) => {
      const newIndex = prevIndex - 1;
      return newIndex < -1 ? -1 : newIndex;
    });
    setDraggedCard({ cards: [card], fromCol: 'deck', cardIndex: currentDeckIndex, originalDeckIndex });
    setDraggedCardPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleTouchStart = (e, card, colIndex, cardIndex) => {
    console.log('Touch start', { card, colIndex, cardIndex });
    e.preventDefault();
    if (!card.faceUp) return;
    soundEffects.play('card');
    const cardsToDrag = columns[colIndex].slice(cardIndex);
    setDraggedCard({ cards: cardsToDrag, fromCol: colIndex, startIndex: cardIndex });
    setOriginalColumn(colIndex);
    setDraggedCardPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    cardsToDrag.forEach((_, idx) => {
      const cardElement = document.querySelector(`[data-card-index="${cardIndex + idx}"][data-col-index="${colIndex}"]`);
      if (cardElement) {
        cardElement.classList.add('hidden-card');
      }
    });
  };

  const handleTouchMove = (e) => {
    setDraggedCardPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (!draggedCard) return;
    const { x, y } = draggedCardPosition;
    console.log('Touch end position:', x, y);
    const isInFoundationZone = (x, y) => {
      const foundationEls = document.querySelectorAll('.foundation-pile');
      for (let i = 0; i < foundationEls.length; i++) {
        const foundation = foundationEls[i];
        const rect = foundation.getBoundingClientRect();
        const extendedZone = { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom + 100 };
        if (x >= extendedZone.left && x <= extendedZone.right && y >= extendedZone.top && y <= extendedZone.bottom) {
          return i;
        }
      }
      return -1;
    };
    const getDropTarget = (x, y) => {
      let element = document.elementFromPoint(x, y);
      console.log('Initial element:', element);
      while (element && element.classList.contains('card-face') && !element.closest('.card-column')) {
        y -= 20;
        element = document.elementFromPoint(x, y);
        console.log('Searching upwards, new element:', element);
      }
      return element;
    };
    const dropTarget = getDropTarget(x, y);
    console.log('Final drop target:', dropTarget);
    const foundationZoneIndex = isInFoundationZone(x, y);
    const dropColIndex = dropTarget?.closest('.card-column')?.dataset?.colIndex;
    if (foundationZoneIndex !== -1) {
      handleFoundationDrop(foundationZoneIndex, e);
    } else if (dropColIndex !== undefined && dropColIndex !== originalColumn) {
      handleDrop(Number(dropColIndex));
    } else {
      if (draggedCard.fromCol === 'deck') {
        setCurrentDeckIndex(draggedCard.originalDeckIndex);
      }
      setDraggedCard(null);
      setDraggedCardPosition({ x: 0, y: 0 });
    }
    if (draggedCard.fromCol !== 'deck') {
      draggedCard.cards.forEach((_, idx) => {
        const hiddenCard = document.querySelector(
          `[data-card-index="${draggedCard.startIndex + idx}"][data-col-index="${draggedCard.fromCol}"]`
        );
        if (hiddenCard) {
          hiddenCard.classList.remove('hidden-card');
        }
      });
    }
  };

  const handleDrop = (dropColIndex) => {
    const { cards, fromCol, startIndex, originalDeckIndex } = draggedCard;
    let newColumns = [...columns];
    let newDeck = [...deck];
    let moveWasValid = false;
    console.log('handleDrop', dropColIndex, cards, newColumns[dropColIndex]);
    if (isValidMove(cards[0], newColumns[dropColIndex])) {
      moveWasValid = true;
      soundEffects.play('card');
      soundEffects.play('goodMove');
      if (fromCol === 'deck') {
        const cardIndex = newDeck.findIndex(
          deckCard => deckCard.suit === cards[0].suit && deckCard.value === cards[0].value
        );
        if (cardIndex !== -1) {
          newDeck.splice(cardIndex, 1);
          setDeck(newDeck);
        }
      } else {
        newColumns[fromCol] = newColumns[fromCol].slice(0, startIndex);
        if (newColumns[fromCol].length > 0) {
          setTimeout(() => {
            setColumns(prevColumns => {
              const updatedColumns = [...prevColumns];
              updatedColumns[fromCol][updatedColumns[fromCol].length - 1].faceUp = true;
              return updatedColumns;
            });
          }, 333);
        }
      }
      newColumns[dropColIndex] = [...newColumns[dropColIndex], ...cards];
      setColumns(newColumns);
      const cardValueElement = document.querySelector('.card-value.top-left');
      if (cardValueElement) {
        const height = cardValueElement.offsetHeight;
        const cardFaces = document.querySelectorAll('.card-face');
        cardFaces.forEach((cardFace) => {
          cardFace.style.marginBottom = `${height + 3}px`;
        });
      }
    } else {
      moveWasValid = false;
      soundEffects.play('card');
      if (fromCol === 'deck') {
        setCurrentDeckIndex(originalDeckIndex);
      }
    }
    setDraggedCard(null);
    setDraggedCardPosition({ x: 0, y: 0 });
    console.log('Updated State:', { deck: newDeck, columns: newColumns, currentDeckIndex });
    return moveWasValid;
  };

  const handleCardClick = (card, colIndex, cardIndex) => {
    if (!card.faceUp && cardIndex === columns[colIndex].length - 1) {
      let newColumns = [...columns];
      newColumns[colIndex][cardIndex].faceUp = true;
      setColumns(newColumns);
    }
  };

  const isValidMove = (card, targetCol) => {
    console.log('isValidMove', card, targetCol);
    if (targetCol.length === 0) return card.value === 'K';
    const topCard = targetCol[targetCol.length - 1];
    const cardColor = getSuitColor(card.suit);
    const topCardColor = getSuitColor(topCard.suit);
    return cardColor !== topCardColor &&
           values.indexOf(card.value) === values.indexOf(topCard.value) - 1;
  };

  const checkWinCondition = () => {
    const totalCards = foundations.reduce((sum, foundation) => sum + foundation.length, 0);
    const isWon = totalCards >= 51; // (update count is 1 off...)
    console.log("Checking win condition:", isWon);
    console.log("Foundations:", foundations);
    console.log("Total cards in foundations:", totalCards);
    if (isWon) {
      console.log("Game won!");
      setIsWin(true);
      soundEffects.play('gameWon');
    }
  };

  return (
    <div className="solitaire-container">
        {resumePrompt && (
                <div className="modal-overlay" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                    <div className="modal-content resume-game-modal">
                        <h2>Resume Game?</h2>
                        <p>Would you like to continue your previous game?</p>
                        <div className="modal-buttons">
                            <button 
                                onClick={handleResumeGame}
                                className="play-btn"
                            >
                                Resume Game
                            </button>
                            <button 
                                onClick={handleNewGame}
                                className="back-btn"
                            >
                                New Game
                            </button>
                        </div>
                    </div>
                </div>
            )}
      {isWin && (
        <>
          <Confetti />
          <div className="win-message">YOU WIN!</div>
        </>
      )}
      <div className={`game-board ${isWin ? 'win' : ''}`}>
        <div className={`deck-stack ${deck[currentDeckIndex] ? 'bottom-margin' : ''}`} onClick={handleDeckClick}></div>
        <div 
          className={`card-face from-deck ${currentDeckIndex === -1 ? 'hidden' : ''}`} 
          onTouchStart={handleDeckTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ 
            color: currentDeckIndex >= 0 ? getSuitColor(deck[currentDeckIndex].suit) : 'black',
            display: currentDeckIndex === -1 ? 'none' : 'block'
          }}
        >
          <div className="card-value top-left">
            {currentDeckIndex >= 0 && `${deck[currentDeckIndex].value}${getSuitSymbol(deck[currentDeckIndex].suit)}`}
          </div>
          <div className="card-value bottom-right">
            {currentDeckIndex >= 0 && `${deck[currentDeckIndex].value}${getSuitSymbol(deck[currentDeckIndex].suit)}`}
          </div>
        </div>
        <div className="foundations">
          {foundations.map((foundation, index) => (
            <div 
              key={index} 
              className="foundation-pile" 
              onTouchEnd={(e) => handleFoundationDrop(index, e)}
              data-foundation-index={index}
            >
              {foundation.length > 0 && (
                <div 
                  className="foundation-card"
                  style={{ color: foundation[foundation.length - 1].color }}
                >
                  <div className="card-value top-left">
                    {foundation[foundation.length - 1].value}{getSuitSymbol(foundation[foundation.length - 1].suit)}
                  </div>
                  <div className="card-value bottom-right">
                    {foundation[foundation.length - 1].value}{getSuitSymbol(foundation[foundation.length - 1].suit)}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="card-columns">
          {columns.map((col, colIndex) => (
            <div
              key={colIndex}
              className={`card-column ${isWin ? 'win-column' : ''}`}
              data-col-index={colIndex}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {col.map((card, cardIndex) => (
                <div
                  key={cardIndex}
                  className={card.faceUp ? "card-face" : "card-back"}
                  data-card-index={cardIndex}
                  data-col-index={colIndex}
                  onTouchStart={(e) => handleTouchStart(e, card, colIndex, cardIndex)}
                  onClick={() => handleCardClick(card, colIndex, cardIndex)}
                  style={{ color: getSuitColor(card.suit) }}
                >
                  {card.faceUp && (
                    <>
                      <div className="card-value top-left">
                        {card.value}{getSuitSymbol(card.suit)}
                      </div>
                      <div className="card-value bottom-right">
                        {card.value}{getSuitSymbol(card.suit)}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {draggedCard && (
        <div
          id="dragged-card"
          style={{
            position: 'absolute',
            left: `${draggedCardPosition.x - 30}px`,
            top: `${draggedCardPosition.y - 80}px`,
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          {draggedCard.cards.map((card, index) => (
            <div
              key={index}
              style={{
                backgroundColor: 'white',
                border: '1px solid #000',
                width: 'calc(100vw / 7 - 25px)',
                height: 'calc((100vw / 7 - 25px) * 1.4)',
                position: 'absolute',
                top: `${index * 20}px`,
                left: 0,
                zIndex: 1000 - index,
                padding: '10px',
                borderRadius: '5px',
                color: getSuitColor(card.suit)
              }}
            >
              <div className="card-value top-left">
                {card.value}{getSuitSymbol(card.suit)}
              </div>
              <div className="card-value bottom-right">
                {card.value}{getSuitSymbol(card.suit)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Solitaire;
